// /src/renderer/App.tsx
import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { HashRouter } from 'react-router-dom';
import AppRoutes from './app/AppRoutes';
import { store } from './app/store';
import { ModalProvider } from '@components/Modal/ModalProvider';
import {
  BufferPool,
  BufferPoolContext,
  ContextPool,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { BranchInfoProvider } from '@contexts/BranchContext';
import { registerDefaultPanels } from '@features/analysis/rendering/registerPanels';
import {
  applyDisplayRuntimeProfileToDocument,
  applyDisplayRuntimeProfileToDocumentAsync,
} from 'config/displayRuntime';

const App: React.FC = (): React.JSX.Element => {
  const [, setDisplayProfileRevision] = React.useState(0);
  const [bufferPool, setBufferPool] = React.useState<BufferPool>({});
  const [contextPool, setContextPool] = React.useState<ContextPool>({
    'CPR.0.stretched': null,
    'CPR.0.straightened': null,
    'CrossSection.0.0': null,
    'CrossSection.0.1': null,
    'CrossSection.0.2': null,
    // 'CPR.1.stretched' :null,
    'CPR.1.straightened': null,
    'CrossSection.1.0': null,
    'CrossSection.1.1': null,
    'CrossSection.1.2': null,
    // 'CPR.2.stretched' :null,
    'CPR.2.straightened': null,
    'CrossSection.2.0': null,
    'CrossSection.2.1': null,
    'CrossSection.2.2': null,
    // 'CPR.3.straightened' :null,
    // 'CrossSection.3.0' :null,
    VR: null,
    Surface3D: null,
  });
  registerDefaultPanels();
  useEffect(() => {
    let cancelled = false;

    const refreshDisplayProfile = async () => {
      applyDisplayRuntimeProfileToDocument();
      await applyDisplayRuntimeProfileToDocumentAsync();
      if (!cancelled) {
        setDisplayProfileRevision((v) => v + 1);
      }
    };

    refreshDisplayProfile();
    const onResize = () => {
      refreshDisplayProfile();
    };
    window.addEventListener('resize', onResize);

    const offDisplayChanged = window.electron?.ipcRenderer?.on?.(
      'display:changed',
      () => {
        refreshDisplayProfile();
      },
    );

    const onResolutionChange = () => {
      refreshDisplayProfile();
    };
    const resolutionMediaQuery = window.matchMedia?.(
      `(resolution: ${window.devicePixelRatio || 1}dppx)`,
    );
    resolutionMediaQuery?.addEventListener?.('change', onResolutionChange);

    const onWheel = (e: WheelEvent) => {
      // Chrome�� Ʈ���е� pinch-zoom�� ctrlKey = true�� �Ѿ��
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      if (!ctrlOrMeta) return;

      // Ctrl/Cmd + (+, -, =, _, 0) �� ���е�
      const key = e.key;
      const block =
        key === '+' ||
        key === '-' ||
        key === '=' ||
        key === '_' ||
        key === '0' ||
        e.code === 'NumpadAdd' ||
        e.code === 'NumpadSubtract' ||
        e.code === 'Numpad0';
      if (block) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // 확대축소 요구사항
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKeyDown, { capture: true });

    const blockMouseBackForward = (event: MouseEvent) => {
      if (event.button === 3 || event.button === 4) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('mousedown', blockMouseBackForward, true);
    window.addEventListener('mouseup', blockMouseBackForward, true);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      offDisplayChanged?.();
      resolutionMediaQuery?.removeEventListener?.('change', onResolutionChange);
      window.removeEventListener('wheel', onWheel as any);
      window.removeEventListener('keydown', onKeyDown as any, true);
      window.removeEventListener('mousedown', blockMouseBackForward, true);
      window.removeEventListener('mouseup', blockMouseBackForward, true);
    };
  }, []);
  return (
    <Provider store={store}>
        <HashRouter>
          <BufferPoolContext.Provider
            value={{ bufferPool, contextPool, setBufferPool, setContextPool }}
          >
            <BranchInfoProvider>
              <ModalProvider>
                <AppRoutes />
              </ModalProvider>
            </BranchInfoProvider>
          </BufferPoolContext.Provider>
        </HashRouter>
    </Provider>
  );
};
if (module.hot) {
  module.hot.accept((err) => {
    if (err) console.error('HMR accept error', err);
    else console.log('HMR updated!');
  });
  module.hot.dispose(() => {
    console.log('HMR disposed, cleaning up…');
  });
}

export default App;
